/*
 * Copyright (c) 2025 - 2026. Bolmate B.V.
 */
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {NotificationType} from '../../../util/enums';

import Notification from '../../../models/notificationModel';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import {NotificationProps, NotificationTypeProps} from '../notificationsPage';

import useDefaultNotification from '../useDefaultNotification';

import NotificationProducts from '../notificationComponents/notificationProducts';

import NotificationActions from '../notificationComponents/notificationActions';

import NotificationChannelsSelect from '../notificationComponents/notificationChannelsSelect';

import NotificationIntervalComp from '../notificationComponents/notificationInterval';

import NotificationChannelsBadge from '../notificationComponents/notificationChannelsBadge';

function OrderNotification(props: NotificationProps) {
    const {t} = useTranslation();
    const {
        notification,
        loading,
        onDeleteNotification,
        onSendTestNotification,
        canEdit,
        canDelete,
        onChangeNotification,
        onReplaceNotification
    } = props;
    const [onChangeEmailAddresses] = useDefaultNotification({...props});

    function renderTitle() {
        return (
            <div className='notification-title beta'>
                <AwesomeIcon name='truck-fast' className='type-icon' />{' '}
                {t('notifications.order.title', 'Order notificatie')}{' '}
                <NotificationChannelsBadge notification={notification} />
            </div>
        );
    }

    return (
        <div className='notification-config'>
            {renderTitle()}
            <div className='notification-card'>
                <div className='notification'>
                    <NotificationProducts
                        onSuccess={onReplaceNotification}
                        notification={notification}
                        disabled={loading || !canEdit}
                        text={t('notifications.order.productsText', 'Bij nieuwe orders voor')}
                    />
                    <NotificationChannelsSelect
                        notification={notification}
                        disabled={loading || !canEdit}
                        onChangeEmailAddresses={onChangeEmailAddresses}
                        onReplaceNotification={onReplaceNotification}
                    />
                    <NotificationIntervalComp
                        onChangeNotification={onChangeNotification}
                        disabled={loading || !canEdit}
                        notification={notification}
                        withRealtime
                    />
                </div>
                <NotificationActions
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    notification={notification}
                    canDelete={canDelete}
                    disabled={false}
                />
            </div>
        </div>
    );
}

export default function OrderNotifications(props: NotificationTypeProps) {
    const {notifications} = props;
    const orderNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.ORDER
        );
    }, [notifications]);
    if (!orderNotifications.length) return null;

    function renderNotification(notification: Notification) {
        return <OrderNotification key={notification.id} notification={notification} disabled={false} {...props} />;
    }

    return (
        <div className='notification-type order-notifications'>
            <div className='notifications'>{orderNotifications.map(renderNotification)}</div>
        </div>
    );
}
